import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import Carousel from 'react-material-ui-carousel';
import { defaultConfig } from '../Carousel.Constants';
import {
  CarouselWrapperType,
  FeaturedTrialItemType,
  ArticleListType,
  CRQuestionaaireListType,
  SavedTrialsListItem,
  PersonalizedFeaturedTrialItemType,
  studyQuestionaireType,
} from '../Carousel.types';
import CarouselItem from '../CarouselItem';
import CarouselTabWrapper from '../CarouselTabWrapper';
import FilterChips from '../../FilterChips/FilterChips';
import { getStudyConditionLabel, getStudyNavigateHref } from '../../FilterChips/FilterChips.utils';
import FeaturedTrialsCarouselFooter from '../FeaturedTrialsCarouselFooter/FeaturedTrialsCarouselFooter';
import { useEffect, useState } from 'react';

const CALLER_COMPONENT = {
  ARTICLES: 'Articles',
  FEATURED_TRIALS: 'FeaturedTrials',
  CR_QUESTIONNAIRE: 'CR-Questionaaire',
  SAVED_TRIALS: 'saved-trials',
  PERSONALIZED_FEATURED_TRIALS: 'personalized-featured-trials',
} as const;

const CarouselWrapper = (props: CarouselWrapperType): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const isTablet = useMediaQuery(theme.breakpoints.between(500, 1024));

  //Currently using only for saved trials as the card size is bigger
  const reduceCardCount = useMediaQuery(theme.breakpoints.between(1024, 1200));

  // Using this only for mobile and tablet
  let carouselCardGroups:
    | ArticleListType[]
    | FeaturedTrialItemType[]
    | CRQuestionaaireListType[]
    | PersonalizedFeaturedTrialItemType[] = [];

  const carouselItemGroups: any = [];
  let showCount = false;
  let totalCount = 0;
  let autoplay = false;
  let autoplayInterval = 0;
  //Added Default Values from Config
  const descriptionWordLimit =
    props?.fields?.descriptionWordLimit?.value || defaultConfig.descriptionWordLimit;
  const titleCharacterLimit =
    props?.fields?.titleCharacterLimit?.value || defaultConfig.titleCharacterLimit;
  const descriptionLineLimit =
    props?.fields?.descriptionLineLimit?.value || defaultConfig.descriptionLineLimit;
  const titleLineLimit = props?.fields?.titleLineLimit?.value || defaultConfig.titleLineLimit;

  const isFeaturedTrials = props.callerComponent === CALLER_COMPONENT.FEATURED_TRIALS;
  const showFilterChips = isFeaturedTrials && props.fields?.enableFilterChip?.value;
  const allFeaturedTrialItems =
    isFeaturedTrials && 'featuredTrialList' in (props.fields || {})
      ? (props.fields.featuredTrialList ?? [])
      : [];

  const [filteredFeaturedTrialItems, setFilteredFeaturedTrialItems] =
    useState<FeaturedTrialItemType[]>(allFeaturedTrialItems);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (isFeaturedTrials) {
      setFilteredFeaturedTrialItems(props.fields.featuredTrialList ?? []);
    }
  }, [isFeaturedTrials, isFeaturedTrials ? props.fields.featuredTrialList : undefined]);

  const handleFeaturedTrialFilterChange = (filteredItems: FeaturedTrialItemType[]) => {
    setFilteredFeaturedTrialItems(filteredItems);
    setPage(0);
  };

  if (props.callerComponent === CALLER_COMPONENT.ARTICLES) {
    const data = props?.fields?.articleList ? props?.fields?.articleList : [];
    totalCount = data.length;
    carouselCardGroups = props?.fields?.articleList || [];
    if (totalCount > 0) {
      for (let i = 0; i < totalCount; i += 3) {
        carouselItemGroups.push(props?.fields?.articleList.slice(i, i + 3));
      }
    }
    autoplay = props?.fields?.isAutomaticSlide ? props?.fields?.isAutomaticSlide.value : false;
    autoplayInterval = autoplay
      ? props?.fields?.slideDuration.value != ''
        ? parseInt(props?.fields?.slideDuration.value) * 1000
        : defaultConfig.autoplayInterval
      : 0;
  } else if (props.callerComponent === CALLER_COMPONENT.CR_QUESTIONNAIRE) {
    const data = props?.questionaaireList || [];
    totalCount = data.length;
    carouselCardGroups = props?.questionaaireList || [];
    if (totalCount > 0) {
      for (let i = 0; i < totalCount; i += 3) {
        carouselItemGroups.push(props?.questionaaireList?.slice(i, i + 3));
      }
    }
    autoplay = props?.fields?.isAutomaticSlide ? props?.fields?.isAutomaticSlide.value : false;
    autoplayInterval = autoplay
      ? props?.fields?.slideDuration.value != ''
        ? parseInt(props?.fields?.slideDuration.value) * 1000
        : defaultConfig.autoplayInterval
      : 0;
  } else if (props.callerComponent === CALLER_COMPONENT.SAVED_TRIALS) {
    const data = props?.data || [];
    totalCount = data.length;
    carouselCardGroups = props?.data || [];
    const cardGroupCount = reduceCardCount ? 2 : 3;
    if (totalCount > 0) {
      for (let i = 0; i < totalCount; i += cardGroupCount) {
        carouselItemGroups.push(props?.data?.slice(i, i + cardGroupCount));
      }
    }
  } else if (props.callerComponent === CALLER_COMPONENT.PERSONALIZED_FEATURED_TRIALS) {
    const data = props?.personalizedFeaturedTrialList ? props?.personalizedFeaturedTrialList : [];

    showCount = props?.fields?.showCount?.value ? props?.fields?.showCount?.value : false;
    totalCount = data.length;
    carouselCardGroups = props?.personalizedFeaturedTrialList || [];
    if (totalCount > 0) {
      for (let i = 0; i < totalCount; i += 3) {
        carouselItemGroups.push(props?.personalizedFeaturedTrialList.slice(i, i + 3));
      }
    }
    autoplay = props?.fields?.isAutomaticSlide ? props?.fields?.isAutomaticSlide.value : false;
    autoplayInterval = autoplay
      ? props?.fields?.slideDuration.value != ''
        ? parseInt(props?.fields?.slideDuration.value) * 1000
        : defaultConfig.autoplayInterval
      : 0;
  } else {
    const data = filteredFeaturedTrialItems;
    showCount = props?.fields?.showCount?.value ? props?.fields?.showCount?.value : false;
    totalCount = data.length;
    carouselCardGroups = filteredFeaturedTrialItems;
    if (totalCount > 0) {
      for (let i = 0; i < totalCount; i += 3) {
        carouselItemGroups.push(filteredFeaturedTrialItems.slice(i, i + 3));
      }
    }
    autoplay = props?.fields?.isAutomaticSlide ? props?.fields?.isAutomaticSlide.value : false;
    autoplayInterval = autoplay
      ? props?.fields?.slideDuration.value != ''
        ? parseInt(props?.fields?.slideDuration.value) * 1000
        : defaultConfig.autoplayInterval
      : 0;
  }

  const effectiveShowCount = showFilterChips ? false : showCount;

  const featuredTrialsFields = isFeaturedTrials ? props.fields : null;
  const showingOfTemplate = featuredTrialsFields?.showingOf?.value;
  const viewAllButtonText = featuredTrialsFields?.viewAllButtonText?.value;
  const viewAllButtonHref = featuredTrialsFields?.viewAllButtonLink?.value?.href;
  const visibleCount = carouselItemGroups[page]?.length ?? 0;

  const renderFeaturedTrialsFooter = (showDesktopFeatures: boolean) => {
    if (!isFeaturedTrials) {
      return null;
    }

    return (
      <FeaturedTrialsCarouselFooter
        showingOfTemplate={showingOfTemplate}
        showShowingOf={showDesktopFeatures && Boolean(showingOfTemplate)}
        visibleCount={visibleCount}
        totalCount={totalCount}
        slideCount={carouselItemGroups.length}
        currentPage={page}
        onPageChange={setPage}
        showIndicators={showDesktopFeatures && totalCount > 3}
        viewAllButtonText={viewAllButtonText}
        viewAllButtonHref={viewAllButtonHref}
      />
    );
  };

  const renderArticleViewAll = () => {
    if (props.callerComponent !== CALLER_COMPONENT.ARTICLES) {
      return null;
    }

    return (
      <FeaturedTrialsCarouselFooter
        showShowingOf={false}
        visibleCount={0}
        totalCount={0}
        slideCount={1}
        currentPage={0}
        onPageChange={() => undefined}
        showIndicators={false}
        viewAllButtonText={props.fields.viewAllButtonText?.value}
        viewAllButtonHref={props.fields.viewAllButtonLink?.value?.href}
      />
    );
  };

  return (
    <>
      {showFilterChips && (
        <FilterChips
          items={allFeaturedTrialItems}
          onFilterChange={handleFeaturedTrialFilterChange}
          getConditionLabel={getStudyConditionLabel}
          getNavigateHref={getStudyNavigateHref}
        />
      )}
      {isMobile || isTablet ? (
        // Added this Condition for typescript related changes
        props?.callerComponent === CALLER_COMPONENT.ARTICLES ? (
          <>
            <CarouselTabWrapper
              isMobile={isMobile}
              isTablet={isTablet}
              totalCount={totalCount}
              descriptionWordLimit={descriptionWordLimit}
              descriptionLineLimit={descriptionLineLimit}
              titleCharacterLimit={titleCharacterLimit}
              titleLineLimit={titleLineLimit}
              showCount={showCount}
              callerComponent={props?.callerComponent}
              data={carouselCardGroups as ArticleListType[]}
              imageVariant={props?.params?.imageVariant}
            />
            {renderArticleViewAll()}
          </>
        ) : props?.callerComponent === CALLER_COMPONENT.CR_QUESTIONNAIRE ? (
          <CarouselTabWrapper
            isMobile={isMobile}
            isTablet={isTablet}
            totalCount={totalCount}
            descriptionWordLimit={descriptionWordLimit}
            descriptionLineLimit={descriptionLineLimit}
            titleCharacterLimit={titleCharacterLimit}
            titleLineLimit={titleLineLimit}
            showCount={showCount}
            callerComponent={props?.callerComponent}
            data={carouselCardGroups as studyQuestionaireType[]}
            dataProps={props}
          />
        ) : props?.callerComponent === CALLER_COMPONENT.SAVED_TRIALS ? (
          <CarouselTabWrapper
            isMobile={isMobile}
            isTablet={isTablet}
            totalCount={totalCount}
            descriptionWordLimit={descriptionWordLimit}
            descriptionLineLimit={descriptionLineLimit}
            titleCharacterLimit={titleCharacterLimit}
            titleLineLimit={titleLineLimit}
            showCount={showCount}
            callerComponent={props?.callerComponent}
            data={carouselCardGroups as any}
            modalProps={props?.modalProps}
            state={props?.state}
            tagProps={props?.tagProps}
          />
        ) : props?.callerComponent === CALLER_COMPONENT.PERSONALIZED_FEATURED_TRIALS ? (
          <CarouselTabWrapper
            isMobile={isMobile}
            isTablet={isTablet}
            totalCount={totalCount}
            descriptionWordLimit={descriptionWordLimit}
            descriptionLineLimit={descriptionLineLimit}
            titleCharacterLimit={titleCharacterLimit}
            titleLineLimit={titleLineLimit}
            showCount={showCount}
            callerComponent={props?.callerComponent}
            data={carouselCardGroups as PersonalizedFeaturedTrialItemType[]}
            seeIfYouQualifyText={props?.fields?.seeIfYouQualifyText}
            findOutMoreText={props?.fields?.findOutMoreText}
          />
        ) : (
          <>
            <CarouselTabWrapper
              isMobile={isMobile}
              isTablet={isTablet}
              totalCount={totalCount}
              descriptionWordLimit={descriptionWordLimit}
              descriptionLineLimit={descriptionLineLimit}
              titleCharacterLimit={titleCharacterLimit}
              titleLineLimit={titleLineLimit}
              showCount={effectiveShowCount}
              callerComponent={props?.callerComponent}
              data={carouselCardGroups as FeaturedTrialItemType[]}
            />
            {renderFeaturedTrialsFooter(false)}
          </>
        )
      ) : (
        <>
          <Carousel
            index={isFeaturedTrials ? page : undefined}
            className={props?.identifier}
            NextIcon={
              (totalCount > 3 && (
                <ArrowForwardIosIcon
                  className="next-btn"
                  tabIndex={0}
                  sx={{
                    fontSize: '2.5rem',
                    ...(page == carouselItemGroups.length - 1 && {
                      color: '#979797',
                    }),
                  }}
                />
              )) ||
              ''
            }
            PrevIcon={
              (totalCount > 3 && (
                <ArrowBackIosNewIcon
                  className="prev-btn"
                  tabIndex={0}
                  sx={{
                    fontSize: '2.5rem',
                    ...(page == 0 && {
                      color: '#979797',
                    }),
                  }}
                />
              )) ||
              ''
            }
            sx={{
              maxWidth: '1365px',
              margin: '0 auto',
              ...(page == 0 && {
                '& div:has(button[aria-label="Previous"]):hover button': {
                  opacity: '1 !important',
                },
                '& div:has(button[aria-label="Previous"]):hover svg': {
                  fill: '#979797',
                },
                '& button[aria-label="Previous"] ': {
                  userSelect: 'none',
                  pointerEvents: 'none',
                  cursor: 'default',
                },
              }),
              ...(page == carouselItemGroups.length - 1 && {
                '& div:has(button[aria-label="Next"]):hover button': {
                  opacity: '1 !important',
                },
                '& div:has(button[aria-label="Next"]):hover svg': {
                  fill: '#979797',
                },
                '& button[aria-label="Next"] ': {
                  userSelect: 'none',
                  pointerEvents: 'none',
                  cursor: 'default',
                },
              }),
            }}
            navButtonsAlwaysVisible={true}
            fullHeightHover={false}
            autoPlay={autoplay}
            interval={autoplayInterval}
            indicators={props.fields?.enableFilterChip?.value ? false : totalCount > 3}
            navButtonsWrapperProps={{
              style: {
                top: `calc(50% - 70px)`,
              },
            }}
            navButtonsProps={{
              style: {
                backgroundColor: 'transparent',
                color: theme.palette.primary.main,
                borderRadius: 0,
                margin: 0,
              },
            }}
            indicatorContainerProps={{
              style: {
                marginTop: '1rem',
              },
            }}
            indicatorIconButtonProps={{
              style: {
                color: theme.palette.neutral4.main,
                margin: '2px',
              },
            }}
            activeIndicatorIconButtonProps={{
              style: {
                color: theme.palette.primary.main,
              },
            }}
            onChange={(now) => {
              setPage(now ?? 0);
            }}
          >
            {carouselItemGroups.map(
              (
                carouselItem:
                  | FeaturedTrialItemType[]
                  | ArticleListType[]
                  | CRQuestionaaireListType[]
                  | studyQuestionaireType[]
                  | SavedTrialsListItem[]
                  | PersonalizedFeaturedTrialItemType[],
                carouselItemIndex: number
              ) => {
                return (
                  <Stack
                    key={carouselItemIndex}
                    direction="row"
                    justifyContent="center"
                    sx={{
                      maxWidth: `calc(100vw - 250px)`,
                      ...(props?.callerComponent === CALLER_COMPONENT.SAVED_TRIALS && {
                        maxWidth: 'calc(100vw - 20px)',
                      }),
                      marginInline: 'auto',
                      minHeight:
                        props?.callerComponent === CALLER_COMPONENT.ARTICLES
                          ? '460px'
                          : props?.callerComponent === CALLER_COMPONENT.CR_QUESTIONNAIRE
                            ? 'max-content'
                            : props?.callerComponent ===
                                  CALLER_COMPONENT.PERSONALIZED_FEATURED_TRIALS ||
                                props?.callerComponent === CALLER_COMPONENT.FEATURED_TRIALS
                              ? '600px'
                              : '530px',
                      ...(props?.callerComponent === CALLER_COMPONENT.FEATURED_TRIALS && {
                        gap: 2,
                      }),
                      [theme.breakpoints.between(1024, 1149)]: {
                        ...(props?.callerComponent === CALLER_COMPONENT.FEATURED_TRIALS && {
                          maxWidth: `calc(100vw - 95px)`,
                          marginLeft: '45px',
                        }),
                      },
                      [theme.breakpoints.between(1150, 1300)]: {
                        ...(props?.callerComponent === CALLER_COMPONENT.FEATURED_TRIALS && {
                          maxWidth: `calc(100vw - 170px)`,
                        }),
                      },
                    }}
                  >
                    {/* Added The logic for conditional Rendering with Typescript */}
                    {props?.callerComponent === CALLER_COMPONENT.ARTICLES ? (
                      <CarouselItem
                        isMobile={isMobile}
                        isTablet={isTablet}
                        totalCount={totalCount}
                        slideNumber={carouselItemIndex}
                        data={carouselItem as ArticleListType[]}
                        descriptionWordLimit={descriptionWordLimit}
                        descriptionLineLimit={descriptionLineLimit}
                        titleCharacterLimit={titleCharacterLimit}
                        titleLineLimit={titleLineLimit}
                        showCount={showCount}
                        callerComponent={props.callerComponent}
                        imageVariant={props?.params?.imageVariant}
                      />
                    ) : props?.callerComponent === CALLER_COMPONENT.CR_QUESTIONNAIRE ? (
                      <CarouselItem
                        isMobile={isMobile}
                        isTablet={isTablet}
                        totalCount={totalCount}
                        slideNumber={carouselItemIndex}
                        data={carouselItem as studyQuestionaireType[]}
                        dataProps={props}
                        descriptionWordLimit={descriptionWordLimit}
                        descriptionLineLimit={descriptionLineLimit}
                        titleCharacterLimit={titleCharacterLimit}
                        titleLineLimit={titleLineLimit}
                        showCount={showCount}
                        callerComponent={props.callerComponent}
                      />
                    ) : props?.callerComponent === CALLER_COMPONENT.SAVED_TRIALS ? (
                      <CarouselItem
                        isMobile={isMobile}
                        isTablet={isTablet}
                        totalCount={totalCount}
                        slideNumber={carouselItemIndex}
                        data={carouselItem as SavedTrialsListItem[]}
                        descriptionWordLimit={descriptionWordLimit}
                        descriptionLineLimit={descriptionLineLimit}
                        titleCharacterLimit={titleCharacterLimit}
                        titleLineLimit={titleLineLimit}
                        showCount={showCount}
                        callerComponent={props.callerComponent}
                        modalProps={props?.modalProps}
                        state={props?.state}
                        tagProps={props?.tagProps}
                      />
                    ) : props?.callerComponent === CALLER_COMPONENT.PERSONALIZED_FEATURED_TRIALS ? (
                      <CarouselItem
                        isMobile={isMobile}
                        isTablet={isTablet}
                        totalCount={totalCount}
                        slideNumber={carouselItemIndex}
                        data={carouselItem as PersonalizedFeaturedTrialItemType[]}
                        descriptionWordLimit={descriptionWordLimit}
                        descriptionLineLimit={descriptionLineLimit}
                        titleCharacterLimit={titleCharacterLimit}
                        titleLineLimit={titleLineLimit}
                        showCount={showCount}
                        callerComponent={props.callerComponent}
                        seeIfYouQualifyText={props?.fields?.seeIfYouQualifyText}
                        findOutMoreText={props?.fields?.findOutMoreText}
                      />
                    ) : (
                      <CarouselItem
                        isMobile={isMobile}
                        isTablet={isTablet}
                        totalCount={totalCount}
                        slideNumber={carouselItemIndex}
                        data={carouselItem as FeaturedTrialItemType[]}
                        descriptionWordLimit={descriptionWordLimit}
                        descriptionLineLimit={descriptionLineLimit}
                        titleCharacterLimit={titleCharacterLimit}
                        titleLineLimit={titleLineLimit}
                        showCount={effectiveShowCount}
                        callerComponent={props.callerComponent}
                      />
                    )}
                  </Stack>
                );
              }
            )}
          </Carousel>
          {showFilterChips && renderFeaturedTrialsFooter(true)}
          {renderArticleViewAll()}
        </>
      )}
    </>
  );
};

export default CarouselWrapper;
